import React, { useCallback } from 'react';

// Util
import useCanvasStore from './useCanvasStore';

import {
  edgeTypes,
  proOptions,
  CANVAS_MIN_ZOOM_OUT,
  DEFAULT_VIEWPORT,
  isValidConnection,
  CanvasMinimap,
} from '../../../utils/CanvasUtil';
import ReactFlow, {
  Background,
  Controls,
  ControlButton,
  ConnectionMode,
  applyNodeChanges,
  applyEdgeChanges,
  useStoreApi,
} from 'reactflow';
import { applyDagreLayouting } from '../../../utils/layoutUtils';
import ConnectionLine from './ReactFlowUtil/CustomEdges/ConnectionLine';

// MUI
import { Description, GridView, SpeakerNotesOff, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import { Tooltip } from '@mui/material';
import { draftNodeTypes } from './ReactFlowUtil/CustomNodes/ComponentNodeTypes';

const CanvasWithRFInstance = props => {
    const {
      onConnect,
      onDrop,
      onInit,
      onDragOver,
      handleOnNodeMouseEnter,
      handleOnNodeMouseLeave,
      handleSelectionChange,
      handleSelectionContextMenu,
      handleSelectionStart,
      handleSelectionEnd,
      handleOnPaneClick,
      canvasLocked,
      viewport,
      classes,
      showMinimap,
      setShowMinimap,
      showNotes,
      setShowNotes,
      collapseNodes,
      setCollapseNodes,
      isValidConnection: isValidConnectionProp,
      handleNavigateIntoComponent,
      arrowHeadColor: _arrowHeadColor,
      ...restProps
    } = props;
    const draggingNodes = useCanvasStore(s => s.draggingNodes);
    const edges = useCanvasStore(s => s.edges);
    const nodes = useCanvasStore(s => s.nodes);
    const store = useStoreApi();
    const { addSelectedNodes, resetSelectedElements, getNodes, setNodes } = store.getState();

    const onNodesChange = useCallback(changes => {
      if (changes[0].type === 'select') {
        useCanvasStore.getState().setNodesFromDraggingNodesFn(ns => applyNodeChanges(changes, ns));
        return;
      }

      useCanvasStore.getState().setDraggingNodesFn(ns => applyNodeChanges(changes, ns));
      return;
    }, []);

    // Use the isValidConnection prop from Canvas.js which includes BPMN flag,
    // fallback to local validation if not provided
    const connectionStatus = useCallback(
      connection => {
        if (isValidConnectionProp) {
          return isValidConnectionProp(connection);
        }
        return isValidConnection(connection, draggingNodes, edges);
      },
      [draggingNodes, edges, isValidConnectionProp]
    );

    const onEdgesChange = useCallback(changes => {
      useCanvasStore.getState().setEdgesFn(es => applyEdgeChanges(changes, es));
      return;
    }, []);

    return (
      <ReactFlow
        connectionMode={ConnectionMode.Loose}
        onConnect={onConnect}
        nodes={draggingNodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={draftNodeTypes}
        edgeTypes={edgeTypes}
        onDrop={onDrop}
        onInit={onInit}
        onNodeDragStop={() => {
          useCanvasStore.getState().syncNodes();
        }}
        onNodeMouseEnter={(event, node) => {
          handleOnNodeMouseEnter(node, addSelectedNodes);
        }}
        onNodeMouseLeave={(event, node) => {
          handleOnNodeMouseLeave(node, resetSelectedElements, getNodes, setNodes);
        }}
        onSelectionDragStop={() => {
          useCanvasStore.getState().syncNodes();
        }}
        onSelectionChange={selection => {
          handleSelectionChange(selection, true);
        }}
        onSelectionContextMenu={handleSelectionContextMenu}
        onSelectionStart={handleSelectionStart}
        onSelectionEnd={handleSelectionEnd}
        onPaneClick={handleOnPaneClick}
        onDragOver={onDragOver}
        selectNodesOnDrag={false}
        elementsSelectable={true}
        nodesDraggable={true}
        nodesConnectable={!canvasLocked}
        minZoom={CANVAS_MIN_ZOOM_OUT}
        defaultViewport={viewport ? viewport : DEFAULT_VIEWPORT}
        nodeDragThreshold={5}
        proOptions={proOptions}
        deleteKeyCode={null}
        selectionKeyCode="Shift"
        isValidConnection={connectionStatus}
        zoomOnDoubleClick={false}
        connectionLineComponent={ConnectionLine}
        snapToGrid={true}
        snapGrid={[25, 25]}
      >
        <Background variant="dots" gap={16} color="#868686" />
        <Controls className={classes.controls} position="bottom-right">
          <Tooltip title={'Auto Arrange'} placement={'top'}>
            <span>
              <ControlButton
                onClick={event => {
                  const applyDagreResult = applyDagreLayouting(nodes, edges);
                  useCanvasStore.getState().setNodesAndEdges(applyDagreResult);
                }}
              >
                <GridView sx={{ width: 12, height: 12 }} />
              </ControlButton>
            </span>
          </Tooltip>
          <Tooltip title={'Toggle Minimap'} placement={'top'}>
            <span>
              <ControlButton
                onClick={event => {
                  setShowMinimap(!showMinimap);
                }}
              >
                <BrandingWatermarkIcon />
              </ControlButton>
            </span>
          </Tooltip>
          <Tooltip title={showNotes ? 'Hide Notes' : 'Show Notes'} placement={'top'}>
            <span>
              <ControlButton
                onClick={event => {
                  setShowNotes(!showNotes);
                }}
              >
                {showNotes ? <SpeakerNotesOff sx={{ width: 16, height: 16 }} /> : <Description />}
              </ControlButton>
            </span>
          </Tooltip>
          <Tooltip title={collapseNodes ? 'Expand All Nodes' : 'Collapse Nodes'} placement={'top'}>
            <span>
              <ControlButton
                onClick={() => setCollapseNodes(!collapseNodes)}
              >
                {collapseNodes ? <UnfoldMore sx={{ width: 16, height: 16 }} /> : <UnfoldLess sx={{ width: 16, height: 16 }} />}
              </ControlButton>
            </span>
          </Tooltip>
        </Controls>
        {showMinimap && <CanvasMinimap />}
      </ReactFlow>
    );
};

export default CanvasWithRFInstance;
